/**
 * Contrast subtraction: post-contrast minus pre-contrast, computed on demand.
 *
 * WHAT IT IS FOR
 * On MR, enhancement is the finding. A lesion that is already bright on T1
 * (fat, subacute blood, proteinaceous fluid) hides its enhancement inside that
 * baseline brightness, and mild enhancement against bright surroundings is easy
 * to talk yourself out of. Subtracting the pre-contrast acquisition from the
 * post leaves only what changed, so enhancement reads as signal on a black
 * field instead of "is that brighter than it was?".
 *
 * CT runs through the same path when the geometry matches, but it is a much
 * less common read: CT enhancement is quantified in Hounsfield units off the
 * two series directly, and pre/post CT pairs shift more between acquisitions.
 *
 * WHY DERIVED IMAGE IDS AND NOT A DERIVED VOLUME
 * The reader starts in the 2D stack and rolls into MPR from there. Emitting
 * subtracted IMAGES rather than a subtracted volume means one artifact serves
 * both: the stack viewport displays them directly, and "Open in MPR" builds a
 * volume out of them through the ordinary path, with no branch anywhere for
 * "this is a subtraction". Scroll, window/level, thumbnails, measurement and
 * capture all keep working because a subtraction is just a series of images.
 *
 * REGISTRATION
 * There is none, deliberately. This subtracts pixel-for-pixel on matched
 * slices, which is correct only while the patient has not moved between the two
 * acquisitions. In veterinary imaging that assumption is far safer than in
 * human imaging, because the patient is under general anaesthesia for the whole
 * study. It is still an assumption: a table move, a repositioned limb, or a
 * re-shim between sequences produces bright and dark rims at every
 * high-contrast edge that can mimic enhancement. The result is labelled SUB and
 * carries {@link SubtractionWarning}s when the inputs give reason to doubt
 * alignment, so the reader is told rather than left to infer.
 */
import { cache, utilities as csUtilities, imageLoader, metaData, } from '@cornerstonejs/core';
/** ImageId scheme for a computed subtraction slice. */
export const SUBTRACTION_SCHEME = 'nlsub';
/** Separator between the post and pre imageIds inside a subtraction imageId. */
const PAIR_SEPARATOR = '::';
export const DEFAULT_SUBTRACTION_MODE = 'difference';
/**
 * Can this modality be normalised by the sum of the two acquisitions?
 *
 * Only for non-negative magnitude data. CT (and anything else quantitative and
 * signed) fails: soft tissue sits near 0 HU, so the denominator vanishes and
 * the divided image washes out to a flat field. Verified on a real
 * contrast-enhanced CT, where divided mode rendered the entire study uniform
 * grey while the plain difference showed enhancement correctly.
 */
export function isDividedModeSupported(modality) {
    const m = (modality ?? '').toUpperCase();
    if (!m)
        return false;
    return m === 'MR' || m === 'MRI';
}
/**
 * Denominator floor for the divided mode, as a fraction of the image's own
 * signal scale.
 *
 * Division by a near-zero sum is the failure mode of this technique: in air
 * and background both slices are noise, their sum is tiny, and the ratio
 * explodes to +/-1 everywhere outside the patient. The result is a rim of
 * violent speckle that looks like pathology at the skin line. Voxels whose
 * combined signal falls below this floor are reported as 0 (no measurable
 * change) rather than amplified.
 */
const DIVIDED_NOISE_FLOOR_FRACTION = 0.02;
/**
 * Compose the imageId for one subtracted slice. Both source ids are carried in
 * the id itself, so the loader is stateless and a subtraction id survives a
 * cache purge or a page of instances arriving late.
 */
export function subtractionImageId(postImageId, preImageId, mode = DEFAULT_SUBTRACTION_MODE) {
    // The mode rides in the id so the loader stays stateless and switching modes
    // produces genuinely different ids, which is what makes the stack swap and
    // the image cache behave.
    return `${SUBTRACTION_SCHEME}:${mode}${PAIR_SEPARATOR}${postImageId}${PAIR_SEPARATOR}${preImageId}`;
}
/** Split a subtraction imageId back into its post and pre source ids. */
export function parseSubtractionImageId(imageId) {
    if (!imageId.startsWith(`${SUBTRACTION_SCHEME}:`))
        return null;
    const body = imageId.slice(SUBTRACTION_SCHEME.length + 1);
    const firstSep = body.indexOf(PAIR_SEPARATOR);
    if (firstSep <= 0)
        return null;
    const head = body.slice(0, firstSep);
    if (head === 'difference' || head === 'divided') {
        const rest = body.slice(firstSep + PAIR_SEPARATOR.length);
        const at = rest.indexOf(PAIR_SEPARATOR);
        if (at <= 0)
            return null;
        return {
            mode: head,
            postImageId: rest.slice(0, at),
            preImageId: rest.slice(at + PAIR_SEPARATOR.length),
        };
    }
    // Ids written before the mode existed are plain differences.
    return {
        mode: 'difference',
        postImageId: head,
        preImageId: body.slice(firstSep + PAIR_SEPARATOR.length),
    };
}
export function isSubtractionImageId(imageId) {
    return imageId.startsWith(`${SUBTRACTION_SCHEME}:`);
}
/** Slice position along the normal, used to match pre to post by geometry. */
function slicePosition(imageId) {
    const plane = metaData.get('imagePlaneModule', imageId);
    const ipp = plane?.imagePositionPatient;
    const iop = plane?.imageOrientationPatient;
    if (!ipp || ipp.length < 3)
        return null;
    if (!iop || iop.length < 6)
        return ipp[2];
    // Project onto the slice normal so the match is correct for any obliquity,
    // rather than assuming slices advance along +Z.
    const n = [
        iop[1] * iop[5] - iop[2] * iop[4],
        iop[2] * iop[3] - iop[0] * iop[5],
        iop[0] * iop[4] - iop[1] * iop[3],
    ];
    return ipp[0] * n[0] + ipp[1] * n[1] + ipp[2] * n[2];
}
function frameOfReference(imageId) {
    const plane = metaData.get('imagePlaneModule', imageId);
    return plane?.frameOfReferenceUID;
}
function pixelDims(imageId) {
    const pixel = metaData.get('imagePixelModule', imageId);
    if (!pixel?.rows || !pixel?.columns)
        return null;
    return { rows: pixel.rows, columns: pixel.columns };
}
/**
 * Pair post-contrast slices to pre-contrast slices and produce the subtraction
 * imageIds.
 *
 * Matching is by slice POSITION, not by index. Index matching silently pairs
 * the wrong anatomy the moment the two acquisitions differ in slice count or
 * start position, and produces a subtraction that looks plausible while being
 * anatomically meaningless. Position matching degrades honestly instead: a
 * slice with no counterpart is dropped and counted.
 *
 * Falls back to index order only when neither series carries positions, which
 * is the case for some secondary-capture and screenshot series.
 */
export function planSubtraction(pair) {
    const warnings = [];
    const { postImageIds, preImageIds, mode = DEFAULT_SUBTRACTION_MODE } = pair;
    if (!postImageIds.length || !preImageIds.length) {
        return { imageIds: [], warnings, unmatchedCount: 0 };
    }
    const postFor = frameOfReference(postImageIds[0]);
    const preFor = frameOfReference(preImageIds[0]);
    // Different frames of reference means the scanner itself is not claiming the
    // two acquisitions share a coordinate system.
    if (postFor && preFor && postFor !== preFor)
        warnings.push('different-frame-of-reference');
    const postDims = pixelDims(postImageIds[0]);
    const preDims = pixelDims(preImageIds[0]);
    if (postDims &&
        preDims &&
        (postDims.rows !== preDims.rows || postDims.columns !== preDims.columns)) {
        // In-plane matrices differ, so a pixel-for-pixel subtraction is not
        // meaningful; the caller should refuse rather than render nonsense.
        //
        // Hand the actual numbers back. "Different image sizes" on its own reads
        // as arbitrary, especially to a reader who has checked that the slice
        // COUNTS match and reasonably assumes that is the relevant thing.
        warnings.push('geometry-mismatch');
        return {
            imageIds: [],
            warnings,
            unmatchedCount: postImageIds.length,
            dimensions: { post: postDims, pre: preDims },
        };
    }
    if (postImageIds.length !== preImageIds.length)
        warnings.push('slice-count-mismatch');
    const prePositions = preImageIds.map(slicePosition);
    const havePositions = prePositions.some((p) => p !== null) && postImageIds.some((id) => slicePosition(id) !== null);
    const imageIds = [];
    let unmatchedCount = 0;
    if (!havePositions) {
        const n = Math.min(postImageIds.length, preImageIds.length);
        for (let i = 0; i < n; i++)
            imageIds.push(subtractionImageId(postImageIds[i], preImageIds[i], mode));
        unmatchedCount = postImageIds.length - n;
        return { imageIds, warnings, unmatchedCount };
    }
    // Half a slice gap: close enough to be the same slice, far enough not to grab
    // its neighbour. Measured on the POST stack, which defines the output grid
    // and is complete by definition. Measuring it on the pre stack would let a
    // MISSING pre slice inflate the gap and therefore widen the tolerance, so the
    // one case the tolerance exists to catch would sail through it.
    const postPositions = postImageIds.map(slicePosition);
    const gaps = [];
    for (let i = 1; i < postPositions.length; i++) {
        const a = postPositions[i - 1];
        const b = postPositions[i];
        if (a !== null && b !== null)
            gaps.push(Math.abs(b - a));
    }
    const medianGap = gaps.length ? gaps.toSorted((a, b) => a - b)[Math.floor(gaps.length / 2)] : 1;
    const tolerance = Math.max(medianGap / 2, 1e-3);
    for (let p = 0; p < postImageIds.length; p++) {
        const postId = postImageIds[p];
        const target = postPositions[p];
        if (target === null) {
            unmatchedCount++;
            continue;
        }
        let bestIdx = -1;
        let bestDist = Infinity;
        for (let i = 0; i < preImageIds.length; i++) {
            const p = prePositions[i];
            if (p === null)
                continue;
            const dist = Math.abs(p - target);
            if (dist < bestDist) {
                bestDist = dist;
                bestIdx = i;
            }
        }
        if (bestIdx < 0 || bestDist > tolerance) {
            unmatchedCount++;
            continue;
        }
        imageIds.push(subtractionImageId(postId, preImageIds[bestIdx], mode));
    }
    return { imageIds, warnings, unmatchedCount };
}
/**
 * Subtract two pixel arrays into a Float32Array.
 *
 * Float32 rather than the sources' integer type because the result is SIGNED:
 * tissue that loses signal is genuinely negative, and clamping it to zero
 * would hide exactly the washout that makes some lesions interesting.
 * Rescale slope/intercept are applied first so both sides are in the same
 * units before subtracting.
 */
export function subtractPixels(post, pre, postScale, preScale, mode = DEFAULT_SUBTRACTION_MODE) {
    const n = Math.min(post.length, pre.length);
    const out = new Float32Array(n);
    const identityScale = postScale.slope === preScale.slope && postScale.intercept === preScale.intercept;
    if (mode === 'difference') {
        if (identityScale) {
            // Same scaling on both sides cancels exactly, so skip the per-voxel work.
            for (let i = 0; i < n; i++)
                out[i] = post[i] - pre[i];
            return out;
        }
        for (let i = 0; i < n; i++) {
            const a = post[i] * postScale.slope + postScale.intercept;
            const b = pre[i] * preScale.slope + preScale.intercept;
            out[i] = a - b;
        }
        return out;
    }
    // Divided: (post - pre) / (post + pre), bounded to [-1, 1].
    //
    // The floor is derived from THIS pair's own signal, not a fixed number,
    // because MR intensities are arbitrary units that vary by scanner, coil and
    // sequence: a constant would suppress everything on one study and nothing on
    // the next.
    let maxSum = 0;
    for (let i = 0; i < n; i++) {
        const a = identityScale ? post[i] : post[i] * postScale.slope + postScale.intercept;
        const b = identityScale ? pre[i] : pre[i] * preScale.slope + preScale.intercept;
        const sum = Math.abs(a + b);
        if (sum > maxSum)
            maxSum = sum;
    }
    const floor = maxSum * DIVIDED_NOISE_FLOOR_FRACTION;
    for (let i = 0; i < n; i++) {
        const a = identityScale ? post[i] : post[i] * postScale.slope + postScale.intercept;
        const b = identityScale ? pre[i] : pre[i] * preScale.slope + preScale.intercept;
        const sum = a + b;
        // Below the floor both slices are noise; reporting 0 keeps background
        // black instead of turning it into a field of +/-1 speckle.
        out[i] = Math.abs(sum) < floor || sum === 0 ? 0 : (a - b) / sum;
    }
    return out;
}
async function loadSource(imageId) {
    const cached = cache.getImage(imageId);
    if (cached)
        return cached;
    return imageLoader.loadAndCacheImage(imageId);
}
/**
 * The image loader behind the `nlsub:` scheme: load both sources, subtract,
 * and present the result as an ordinary Cornerstone image that borrows the
 * post-contrast slice's geometry.
 */
function loadSubtractionImage(imageId) {
    const parsed = parseSubtractionImageId(imageId);
    if (!parsed) {
        return { promise: Promise.reject(new Error(`Not a subtraction imageId: ${imageId}`)) };
    }
    const promise = (async () => {
        const [post, pre] = await Promise.all([
            loadSource(parsed.postImageId),
            loadSource(parsed.preImageId),
        ]);
        const postPixels = post.getPixelData();
        const prePixels = pre.getPixelData();
        const pixelData = subtractPixels(postPixels, prePixels, { slope: post.slope ?? 1, intercept: post.intercept ?? 0 }, { slope: pre.slope ?? 1, intercept: pre.intercept ?? 0 }, parsed.mode);
        let min = Infinity;
        let max = -Infinity;
        for (let i = 0; i < pixelData.length; i++) {
            const v = pixelData[i];
            if (v < min)
                min = v;
            if (v > max)
                max = v;
        }
        if (!Number.isFinite(min))
            min = 0;
        if (!Number.isFinite(max))
            max = 0;
        const { lower, upper } = subtractionWindow(pixelData, parsed.mode);
        // The renderer reads pixels through the image's VOXEL MANAGER, not through
        // getPixelData, so the subtraction needs its own. Spreading the source
        // image alone carried the post-contrast slice's voxelManager across and the
        // pane displayed the unsubtracted image while every other field said
        // otherwise: correct geometry, correct window, wrong pixels.
        const voxelManager = csUtilities.VoxelManager.createImageVoxelManager({
            width: post.columns,
            height: post.rows,
            scalarData: pixelData,
            numberOfComponents: 1,
        });
        // Everything geometric is inherited from the post-contrast slice, so the
        // subtraction hangs, scrolls and reformats exactly like the series it came
        // from. Slope/intercept are identity because the subtraction is already in
        // rescaled units.
        const image = {
            ...post,
            imageId,
            voxelManager,
            getPixelData: () => pixelData,
            minPixelValue: min,
            maxPixelValue: max,
            slope: 1,
            intercept: 0,
            windowCenter: (lower + upper) / 2,
            windowWidth: upper - lower,
            sizeInBytes: pixelData.byteLength,
            // The stack viewport reads this to pick its texture format; a signed
            // float result must not be presented as the source's integer type.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...{ dataType: 'Float32Array' },
        };
        return image;
    })();
    return { promise };
}
let registered = false;
/**
 * Register the subtraction image loader and its metadata provider.
 *
 * The metadata provider is what lets a subtraction stack build an MPR volume:
 * the volume builder reads imagePlaneModule for every imageId, and without this
 * a `nlsub:` id would resolve to nothing and the volume would have no geometry.
 * Every module simply defers to the post-contrast source.
 */
export function registerSubtractionLoader() {
    if (registered)
        return;
    registered = true;
    imageLoader.registerImageLoader(SUBTRACTION_SCHEME, loadSubtractionImage);
    metaData.addProvider((type, ...args) => {
        const imageId = args[0];
        if (typeof imageId !== 'string')
            return undefined;
        const parsed = parseSubtractionImageId(imageId);
        if (!parsed)
            return undefined;
        // Defer wholesale to the post-contrast slice: same plane, same pixel
        // spacing, same patient. Only the pixel values differ.
        return metaData.get(type, parsed.postImageId);
        // Above the wadouri provider's priority so the `nlsub:` prefix is resolved
        // here rather than being handed to a loader that cannot parse it.
    }, 200);
}
/**
 * Fraction of the changed signal the window covers.
 *
 * Tuned against a real contrast CT rather than picked a priori. At 0.995 the
 * ceiling landed at ~836 HU, set by bone edges, misregistration rims and the
 * densest contrast, so ordinary enhancement rendered around 20% grey and the
 * image read as "nothing there". The top few percent of a difference image is
 * artifact and extreme contrast; letting it saturate is the right trade, and
 * puts genuine enhancement in the bright half of the range where a reader
 * actually sees it.
 */
const WINDOW_PERCENTILE = 0.97;
/** Bins for the percentile histogram: fine enough to place the ceiling, cheap
 *  enough to run per slice in a single pass. */
const WINDOW_HISTOGRAM_BINS = 1024;
/**
 * Display ceiling for the divided mode, as a fraction: 0.5 means a 50% change
 * reaches full white. Chosen to put the clinically interesting band (a few
 * percent up to about half) across the visible range.
 */
const DIVIDED_WINDOW_CEILING = 0.5;
export function subtractionWindow(a, b) {
    // Divided output is a unitless fraction bounded to [-1, 1], so it gets a
    // FIXED window rather than a data-driven one. Two reasons. First, a
    // percentile cannot work here: tissue boundaries legitimately saturate at
    // +/-1, and there are far more than 0.5% of them, so any high percentile
    // lands near 1 and renders a 20% enhancement at a fifth of the range.
    // Second, and better: because the values are normalised, a fixed window
    // means a given grey is the SAME percentage change on every study, scanner
    // and coil, which a raw-intensity subtraction can never offer. The ceiling
    // sits at a 50% change; anything above that is emphatic and can saturate.
    if (b === 'divided')
        return { lower: -DIVIDED_WINDOW_CEILING * 0.15, upper: DIVIDED_WINDOW_CEILING };
    if (typeof a === 'number') {
        // Min/max form: no distribution available, so fall back to the extent.
        const extent = Math.max(Math.abs(a), Math.abs(typeof b === 'number' ? b : 0));
        if (!Number.isFinite(extent) || extent === 0)
            return { lower: -1, upper: 1 };
        const upper = extent * 0.85;
        return { lower: -upper * 0.15, upper };
    }
    const values = a;
    if (values.length === 0)
        return { lower: -1, upper: 1 };
    // Find the NO-CHANGE level and centre on it, rather than assuming it is 0.
    //
    // Two acquisitions of the same patient rarely differ by exactly zero where
    // nothing changed: CT pads outside the reconstruction circle with a value
    // that can differ between series, and scanners drift, so unchanged tissue
    // and air land on a small constant offset instead. Anchoring the window at
    // literal zero then renders that offset as a flat MID-GREY field over the
    // whole image, which is what a subtraction is supposed to make black. The
    // most common value in the image IS "nothing changed" (the vast majority of
    // any subtraction is unchanged tissue), so it is the honest origin.
    const offsetBins = new Uint32Array(WINDOW_HISTOGRAM_BINS);
    let vMin = Infinity;
    let vMax = -Infinity;
    for (let i = 0; i < values.length; i++) {
        const v = values[i];
        if (v < vMin)
            vMin = v;
        if (v > vMax)
            vMax = v;
    }
    let noChange = 0;
    if (Number.isFinite(vMin) && Number.isFinite(vMax) && vMax > vMin) {
        const oScale = (WINDOW_HISTOGRAM_BINS - 1) / (vMax - vMin);
        for (let i = 0; i < values.length; i++) {
            offsetBins[Math.round((values[i] - vMin) * oScale)]++;
        }
        let peak = 0;
        let peakCount = -1;
        for (let i = 0; i < offsetBins.length; i++) {
            if (offsetBins[i] > peakCount) {
                peakCount = offsetBins[i];
                peak = i;
            }
        }
        noChange = vMin + peak / oScale;
    }
    // Histogram the SIGNAL-BEARING voxels only.
    //
    // A subtraction is mostly empty: unchanged tissue is ~0 and the divided mode
    // zeroes sub-noise background outright, so half or more of the array can be
    // exactly zero. Taking a percentile over the whole array lets that empty
    // space consume the budget, dragging the ceiling far into the sparse tail
    // and leaving the actual signal rendered as black. Counting only non-zero
    // voxels makes the percentile describe the CHANGES, which is what the window
    // exists to display.
    // Deviations are measured FROM the no-change level, not from zero.
    let maxDev = 0;
    for (let i = 0; i < values.length; i++) {
        const d = Math.abs(values[i] - noChange);
        if (d > maxDev)
            maxDev = d;
    }
    if (!Number.isFinite(maxDev) || maxDev === 0)
        return { lower: noChange - 1, upper: noChange + 1 };
    const bins = new Uint32Array(WINDOW_HISTOGRAM_BINS);
    const scale = (WINDOW_HISTOGRAM_BINS - 1) / maxDev;
    let counted = 0;
    for (let i = 0; i < values.length; i++) {
        const d = Math.abs(values[i] - noChange);
        if (d === 0)
            continue;
        bins[Math.round(d * scale)]++;
        counted++;
    }
    if (counted === 0)
        return { lower: noChange - 1, upper: noChange + 1 };
    const target = counted * WINDOW_PERCENTILE;
    let seen = 0;
    let ceilingBin = WINDOW_HISTOGRAM_BINS - 1;
    for (let i = 0; i < bins.length; i++) {
        seen += bins[i];
        if (seen >= target) {
            ceilingBin = i;
            break;
        }
    }
    // A near-empty difference (an identical pair) lands every value in bin 0;
    // fall back to the true extent so the window is never zero-width.
    const extent = ceilingBin > 0 ? ceilingBin / scale : maxDev;
    return { lower: noChange - extent * 0.15, upper: noChange + extent };
}
export const __testing = {
    slicePosition,
    loadSubtractionImage,
    resetRegistration: () => {
        registered = false;
    },
};
